import { ReactNode, useEffect, useState } from 'react';
import languages from 'src/locales';
import { setAppMode } from 'src/store/actions/appMode';
import { setWidget } from 'src/store/actions/widget';
import { mapClientSettings } from 'src/utils/mapClientSettings';
import { resolveClientId } from 'src/utils/resolveClientId';
import { resolveLanguage } from 'src/utils/resolveLanguage';
import { updateIntl, useAppDispatch } from 'universal-scripts';

const SUPPORTED_LOCALES = Object.keys(languages);

const detectEmbedded = (): boolean => {
	try {
		if (window.self !== window.top) return true;
	} catch {
		return true;
	}
	const params = new URLSearchParams(window.location.search);
	return params.get('embedded') === '1';
};

const loadEnvConfig = (): Promise<void> => {
	if (window._env_) return Promise.resolve();
	return new Promise((resolve) => {
		const script = document.createElement('script');
		script.src = '/env-config.js';
		script.onload = () => resolve();
		script.onerror = () => resolve();
		document.head.appendChild(script);
	});
};

const Bootstrap = ({ children }: { children: ReactNode }) => {
	const dispatch = useAppDispatch();
	const [ready, setReady] = useState(false);

	useEffect(() => {
		const init = async () => {
			await loadEnvConfig();

			const params = new URLSearchParams(window.location.search);
			const isEmbedded = detectEmbedded();
			const mode = isEmbedded ? 'embedded' : 'standalone';
			dispatch(setAppMode(mode));

			const clientId = resolveClientId({
				urlClientId: params.get('client'),
				envClientId: window._env_?.CLIENT_ID
			});

			const finalize = (clientConfig: ReturnType<typeof mapClientSettings>) => {
				if (clientConfig) dispatch(setWidget(clientConfig));
				const lang = resolveLanguage({
					urlLang: params.get('lang'),
					scriptLang: null,
					clientLang: clientConfig?.language,
					multilingual: clientConfig?.multilingual,
					navigatorLang: navigator.language,
					supported: SUPPORTED_LOCALES
				});
				dispatch(updateIntl({ lang, messages: (languages as Record<string, unknown>)[lang] as Record<string, string> }));
				setReady(true);
			};

			if (!clientId) {
				finalize(null);
				return;
			}

			const baseApi = window?._env_?.API_URL;
			try {
				const response = await fetch(`${baseApi}/client/settings/?id=${clientId}`);
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				const payload = await response.json();
				finalize(mapClientSettings(payload));
			} catch (err) {
				console.warn('[Bootstrap] failed to load client settings:', err);
				finalize(null);
			}
		};

		init();
	}, [dispatch]);

	if (!ready) return null;
	return <>{children}</>;
};

export default Bootstrap;
